import {
  getPersistedSelectedDayRate,
  normalizeSelectedDayRate,
} from "./dayRateUtils";

const toAmount = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : 0;
};

// Rounds to cents, half away from zero. Summing fractional charges leaves
// floating-point noise (e.g. 10.049999999999999 for 10.05), so the value is
// first trimmed to 12 significant digits.
export const roundCurrency = (value) => {
  const amount = Number(toAmount(value).toPrecision(12));
  if (Math.abs(amount) < 0.000001) return 0;
  const cents = Math.round(Number(`${Math.abs(amount)}e2`));
  return Math.sign(amount) * Number(`${cents}e-2`);
};

// Broker charges split by each line's day rate type, following the invoice
// editor's line charge rule: an unknown or "Default" selection bills at the
// billable rate, and adjustment lines with negative hours reduce the charge.
// Groups keep the order in which their rate type first appears.

export const getBrokerChargeBreakdown = (lineItems = [], ratesBySelection = {}) => {
  const hoursBySelection = new Map();

  (Array.isArray(lineItems) ? lineItems : []).forEach((item) => {
    const hours = Number(item?.Hours ?? 0);
    if (!Number.isFinite(hours) || hours === 0) return;

    const selection = normalizeSelectedDayRate(getPersistedSelectedDayRate(item));
    hoursBySelection.set(selection, (hoursBySelection.get(selection) || 0) + hours);
  });

  return [...hoursBySelection.entries()].map(([selection, hours]) => {
    const rate = toAmount(ratesBySelection[selection]);
    return { selection, hours, rate, charge: (hours / 8) * rate };
  });
};

export const formatBrokerChargeLine = ({ hours, rate, selection }) =>
  `(${hours.toFixed(2)} / 8) * $${rate.toFixed(2)} ${selection.toLowerCase()}`;


export const getBrokerChargeTotal = (brokerCharges = []) =>
  roundCurrency(
    brokerCharges.reduce((total, charge) => total + toAmount(charge.charge), 0)
  );

// Total owed to the broker: broker charges + per diem + lodging, copies and
// misc + miles at the mileage rate. Components are summed unrounded and the
// total is rounded once, the same way the invoice total is saved.
export const getTotalOwed = ({
  brokerCharges = [],
  perDiem,
  lodging,
  copies,
  misc,
  miles,
  mileageRate,
} = {}) =>
  roundCurrency(
    brokerCharges.reduce((total, charge) => total + toAmount(charge.charge), 0) +
      toAmount(perDiem) +
      toAmount(lodging) +
      toAmount(copies) +
      toAmount(misc) +
      toAmount(miles) * toAmount(mileageRate)
  );
